// @ts-ignore: remove after translating into typescript
import { keyMap } from '@locales/lokalise/keyMap';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { AIError } from 'applicationInsights/AIError';
// @ts-ignore: remove after translating into typescript
import { getAppInsights } from 'applicationInsights/TelemetryService';
import { AxiosError } from 'axios';
import { NotificationDialog } from 'components/shared/modal';
import { EmptyObject } from 'components/shared/shared.interfaces';
import { ErrorCodesData, ErrorCodeServerResponse, ErrorCodesInfo } from 'global.interfaces';
import i18next from 'i18next';
import { getErrorInfoByErrorCode } from 'utils/errorCodesMap';

const DEFENDER_THROW_EXCEPTION = 'MembersArea.Common.Core.Defender.ThrowException';
const SQL_EXCEPTION = 'MySqlException';
const HTML_ELEMENT = '</html>';

export const ONE_TIME_PASSWORD_REQUIRED = 'user.otpValidationRequired';
export const ONE_TIME_PASSWORD_INVALID_USER = 'user.invalidOtp';
export const ONE_TIME_PASSWORD_LIMIT_EXCEEDED = 'user.invalidOtpLimitExceeded';

export type ErrorType = (AxiosError & { stack?: string }) | null;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TelemetryData = Record<string, any>;

export const handleError = (
  prefix: string | null,
  error?: ErrorType,
  message?: string | null,
  errorCodesMapData?: ErrorCodesData | EmptyObject,
  data?: TelemetryData,
  showTime?: number,
): void => {
  const skipStatuses: (number | undefined)[] = [401, 403, 503];

  if (skipStatuses.includes(error?.response?.status)) {
    return;
  }

  const errorResponseData = error?.response?.data as ErrorCodeServerResponse | string;
  let errorInfo: ErrorCodesInfo | null = null;

  if (typeof errorResponseData !== 'string' && errorResponseData?.errorCode) {
    errorInfo = getErrorInfoByErrorCode(
      errorResponseData.errorCode,
      errorCodesMapData,
      errorResponseData.messagePlaceholders,
    );
  }

  if (errorInfo) {
    registerExceptionInTrackingSystem(errorInfo.message, error, data);
    NotificationDialog.show(errorInfo.message, 'error', {
      onCloseCallback: errorInfo?.callback,
      notificationShowTime: showTime ?? errorInfo?.showTime,
    });

    return;
  }

  handleErrorWithErrorCodeInfo(prefix, error, message, errorResponseData, data, showTime);
};

const handleErrorWithErrorCodeInfo = (
  prefix: string | null,
  error: ErrorType | undefined,
  message: string | undefined | null,
  errorResponseData: ErrorCodeServerResponse | string,
  data?: TelemetryData,
  showTime?: number,
): void => {
  const notificationText = getNotificationText(prefix, message, errorResponseData);

  registerExceptionInTrackingSystem(notificationText, error, data);

  NotificationDialog.show(notificationText, 'error', {
    notificationShowTime: showTime,
  });
};

const getNotificationText = (
  prefix: string | null,
  message: string | undefined | null,
  errorResponseData: ErrorCodeServerResponse | string,
): string => {
  const messageFromServer = typeof errorResponseData === 'object' ? errorResponseData.errorMessage : errorResponseData;

  const msg = message && i18next.t(message as KeyMapKeys);
  const details = msg || messageFromServer || '';

  const errorText = detailsIncludesServerErrors(details) ? i18next.t(keyMap.sharedSystemInternalServerError) : details;

  return prefix ? `${i18next.t(prefix as KeyMapKeys)}. ${errorText}` : errorText;
};

const detailsIncludesServerErrors = (details: string): boolean => {
  return (
    details.includes(DEFENDER_THROW_EXCEPTION) || details.includes(HTML_ELEMENT) || details.includes(SQL_EXCEPTION)
  );
};

export const registerExceptionInTrackingSystem = (
  errorMessage: string,
  error?: ErrorType | Error,
  data?: TelemetryData,
): void => {
  const appInsights = getAppInsights();

  const appInsightsError = new AIError(errorMessage);

  if (error) {
    appInsightsError.stack = error.stack;
  }

  if (appInsights) {
    appInsights.trackException({ error: appInsightsError, severityLevel: SeverityLevel.Error, properties: data });
  }
};

export const trackEventWithDataInTrackingSystem = (name: string, data: TelemetryData): void => {
  const appInsights = getAppInsights();

  if (appInsights) {
    appInsights.trackEvent({ name, properties: data });
  }
};
